/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { useSnackbar } from 'notistack';
import {
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CardHeader,
  Box,
  Divider,
  Checkbox,
  List,
  ListItem,
  InputAdornment,
  IconButton,
  FormControlLabel,
  LinearProgress,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import ClearIcon from '@mui/icons-material/Clear';
import axiosInstance from '../../utils/axiosInstance';

const SensorList = ({ successList, failureList, onRetry }) => (
  <Box>
    <Typography variant="h5">Status</Typography>
    <Divider sx={{ my: 2 }} />
    {successList.length > 0 && (
      <>
        <Typography variant="h6">{`Successful (${successList.length}):`}</Typography>
        <Box sx={{ m: 3 }}>
          <ul>
            {successList.map((sensor) => (
              <li key={sensor.sensorUrl} style={{ color: 'green' }}>
                {sensor.sensorUrl}
              </li>
            ))}
          </ul>
        </Box>
      </>
    )}
    {failureList.length > 0 && (
      <>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">{`Failed (${failureList.length}):`}</Typography>
          <IconButton aria-label="retry" size="small" onClick={onRetry}>
            <ReplayIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ m: 3 }}>
          <ul>
            {failureList.map((sensor) => (
              <li key={sensor.sensorUrl} style={{ color: 'red' }}>
                {sensor.sensorUrl}
              </li>
            ))}
          </ul>
        </Box>
      </>
    )}
  </Box>
);

const UrlInput = () => {
  const [url, setUrl] = React.useState('');
  const [liveUrlList, setLiveUrlList] = React.useState([]);
  const [successfulSensors, setSuccessfulSensors] = React.useState([]);
  const [failedSensors, setFailedSensors] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [checkedUrls, setCheckedUrls] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const { enqueueSnackbar } = useSnackbar();

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const fetchNewList = async () => {
    setTimeout(() => {
      const refreshListButton = document.getElementById('refresh-list-button');
      if (refreshListButton) {
        refreshListButton.click();
      }
    }, 1000);
  };

  const addSensor = async (liveUrl, index, urlsToAdd) => {
    const lastSegment = liveUrl.split('/').filter((segment) => segment !== '').pop();
    const sensorName = lastSegment ? lastSegment.split('.')[0] : 'RTSP';
    const jsonData = { name: sensorName, sensorUrl: liveUrl };

    try {
      await axiosInstance.post(`${window.location.protocol}//${window.location.host}/api/v1/sensor/add`, jsonData);
      setSuccessfulSensors((prevState) => [...prevState, jsonData]);
    } catch (error) {
      setFailedSensors((prevState) => [...prevState, jsonData]);
      const errorMessage = error.response?.data
        ? error.response.data.error_message
        : `Error ${error.response?.status}`;
      enqueueSnackbar(`Error failed to add ${jsonData.sensorUrl} - ${errorMessage}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
    }

    if (index !== urlsToAdd.length - 1) {  // Use urlsToAdd passed as argument
      await sleep(500);
    }
  };

  const addSensorsWithDelay = async (urlsToAdd) => {
    if (urlsToAdd.length > 0) {
      setLoading(true);
    }

    const addSensorsPromises = urlsToAdd.reduce((promiseChain, liveUrl, index) =>
      promiseChain.then(() => addSensor(liveUrl, index, urlsToAdd)),
      Promise.resolve()
    );
  
    await addSensorsPromises;
    setLoading(false);
    fetchNewList();
  };

  const handleCheckboxChange = (url) => {
    setCheckedUrls((prevState) =>
      prevState.includes(url)
        ? prevState.filter((item) => item !== url)
        : [...prevState, url]
    );
  };

  const handleSelectAllChange = (event) => {
    if (event.target.checked) {
      setCheckedUrls(liveUrlList.map(({ url }) => url));
    } else {
      setCheckedUrls([]);
    }
  };

  const handleAddSelectedStreams = () => {
    addSensorsWithDelay(checkedUrls);
  };

  const onRetry = () => {
    setCheckedUrls([]);
    setSuccessfulSensors([]);
    
    const retryUrls = failedSensors.map(sensor => sensor.sensorUrl);
    addSensorsWithDelay(retryUrls);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      enqueueSnackbar(`Entered invalid URL`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      return;
    }
    
    setLoading(true);
    setSuccessfulSensors([]);
    setFailedSensors([]);

    axiosInstance
      .get(`${url}/api/v1/sensor/streams`)
      .then((response) => {
        if (response.data) {
          const liveUrls = response.data.flatMap(sensor => {
            const sensorId = Object.keys(sensor)[0];
            const streams = sensor[sensorId];
            return streams.map(stream => ({
              name: stream.name,
              url: stream.url,
            }));
          }).filter(({ url }) => url); // Filter out empty URLs
          
          setLoading(false);
          setLiveUrlList(liveUrls);
        }
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
        enqueueSnackbar(`Failed to get streams from ${url}`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
      });
  };

  const clearUrl = () => {
    setUrl('');
    setLiveUrlList([]);
    setCheckedUrls([]);
    setSearchQuery('');
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredUrls = liveUrlList.filter(({ name, url }) =>
    name.toLowerCase().includes(searchQuery.toLowerCase()) || url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Typography variant="h4" sx={{ mb: 5 }}>
        Add Streams from Nvstreamer
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={'Enter endpoint to fetch streams'}
              subheader={'Supported format: http://ip:port or https://ip:port'}
            />
            <CardContent>
              <form onSubmit={handleSubmit}>
                <TextField
                  label="URL"
                  fullWidth
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={clearUrl} edge="end">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button variant="contained" color="primary" sx={{ mt: 2 }} type="submit">
                  Submit
                </Button>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {liveUrlList.length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkedUrls.length === filteredUrls.length}
                        onChange={handleSelectAllChange}
                      />
                    }
                    label="Select All"
                  />
                  <TextField
                    label="Search"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearch}
                    sx={{ ml: 2, flex: 1 }}
                  />
                </Box>

                <Box sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white', px: 2, py: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={1} />
                    <Grid item xs={5}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', pl: 2 }}>
                        Video Name
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        RTSP URL
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                  <List>
                    {filteredUrls.map(({ name, url }, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          position: 'relative',
                          py: 0.5,
                          backgroundColor: checkedUrls.includes(url) ? '#e8f0fe' : 'transparent',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          width: '100%',
                        }}
                        onClick={() => handleCheckboxChange(url)}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={1}>
                            <Checkbox
                              edge="start"
                              checked={checkedUrls.includes(url)}
                              onChange={() => handleCheckboxChange(url)}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <Typography>
                              {name}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography>
                              {url}
                            </Typography>
                          </Grid>
                        </Grid>
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button variant="contained" onClick={handleAddSelectedStreams} disabled={checkedUrls.length === 0}>
                    Add Selected Streams
                  </Button>
                </Box>

                {loading && <LinearProgress />}
              </CardContent>
            </Card>
          </Grid>
        )}

        {(successfulSensors.length > 0 || failedSensors.length > 0) && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <SensorList
                  successList={successfulSensors}
                  failureList={failedSensors}
                  onRetry={onRetry}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default UrlInput;

